/**
 * Small presentational primitives for the search_strategy artifact:
 * deterministic-color Avatar + CompanyLogo (real image with monogram
 * fallback, mirroring the juicebox replica's Logo/CompanyLogo), and the
 * Good / Partial / No verdict vocabulary (VerdictDot + FitBadge).
 */

import { useState } from 'react';
import { Building2, Check, Minus } from 'lucide-react';
import type { CriterionVerdict, Verdict } from './types';

export const VC = {
    good: '#1E9E63',
    goodBg: 'rgba(30, 158, 99, 0.14)',
    partial: '#C0891C',
    partialBg: 'rgba(214, 160, 46, 0.16)',
    no: '#9AA1A9',
    noBg: 'rgba(120, 130, 140, 0.12)',
} as const;

const SWATCHES = ['#2F6FED', '#1E9E63', '#C0891C', '#D23B36', '#2AA6A0', '#7A5AD6', '#9333A8', '#0E7490'];

export function hueFor(seed: string): string {
    let h = 0;
    for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
    return SWATCHES[h % SWATCHES.length]!;
}

export function initials(name: string): string {
    const parts = name.replace(/^Dr\.?\s+/i, '').trim().split(/\s+/);
    return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase() || '?';
}

/** Round candidate avatar — real picture when available, monogram otherwise. */
export function Avatar({ name, src, size = 36 }: { name: string; src?: string; size?: number }) {
    const [failed, setFailed] = useState(false);
    const hue = hueFor(name);
    if (src && !failed) {
        return (
            <img
                src={src}
                alt=""
                width={size}
                height={size}
                loading="lazy"
                onError={() => setFailed(true)}
                className="shrink-0 rounded-full object-cover"
                style={{ width: size, height: size, background: 'var(--surface-tertiary)' }}
            />
        );
    }
    return (
        <span
            aria-hidden
            className="inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white"
            style={{ width: size, height: size, background: hue, fontSize: Math.round(size * 0.36) }}
        >
            {initials(name)}
        </span>
    );
}

/**
 * Square company/school mark — real logo when available, otherwise a
 * low-key generic gray building icon (NOT a colored monogram, so the
 * eye goes to people's avatars, not to placeholder company marks).
 */
export function CompanyLogo({ name, src, size = 28, radius = 6 }: { name?: string; src?: string; size?: number; radius?: number }) {
    const [failed, setFailed] = useState(false);
    if (src && !failed) {
        return (
            <span
                className="inline-flex shrink-0 items-center justify-center overflow-hidden"
                style={{ width: size, height: size, borderRadius: radius, background: '#fff', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.06)' }}
            >
                <img
                    src={src}
                    alt={name ?? ''}
                    width={size}
                    height={size}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={() => setFailed(true)}
                    className="block h-full w-full object-contain"
                />
            </span>
        );
    }
    return (
        <span
            aria-hidden
            title={name}
            className="inline-flex shrink-0 items-center justify-center"
            style={{
                width: size,
                height: size,
                borderRadius: radius,
                background: 'var(--surface-secondary)',
                border: '1px solid var(--border-muted)',
                color: 'var(--text-tertiary)',
            }}
        >
            <Building2 size={Math.round(size * 0.52)} strokeWidth={1.7} />
        </span>
    );
}

const VMAP: Record<Verdict, { fg: string; bg: string; label: string }> = {
    good: { fg: VC.good, bg: VC.goodBg, label: 'Good match' },
    partial: { fg: VC.partial, bg: VC.partialBg, label: 'Potential fit' },
    no: { fg: VC.no, bg: VC.noBg, label: 'Not a match' },
};

export function VerdictDot({ v, size = 18, title }: { v: Verdict; size?: number; title?: string }) {
    const t = VMAP[v];
    return (
        <span
            title={title ?? t.label}
            className="inline-flex shrink-0 items-center justify-center rounded-full"
            style={{ width: size, height: size, background: t.bg }}
        >
            {v === 'good' && <Check size={Math.round(size * 0.62)} strokeWidth={2.6} color={t.fg} />}
            {v === 'partial' && (
                <span style={{ width: Math.round(size * 0.34), height: Math.round(size * 0.34), borderRadius: 999, background: t.fg }} />
            )}
            {v === 'no' && <Minus size={Math.round(size * 0.58)} strokeWidth={2.4} color={t.fg} />}
        </span>
    );
}

export function verdictMeta(v: Verdict) {
    return VMAP[v];
}

/** Compute weighted match share 0..1 (good = 1, partial = 0.5). */
export function fitOf(verdicts: CriterionVerdict[] | undefined): number {
    if (!verdicts || verdicts.length === 0) return 0;
    const met = verdicts.reduce((s, v) => s + (v.verdict === 'good' ? 1 : v.verdict === 'partial' ? 0.5 : 0), 0);
    return met / verdicts.length;
}

export function fitWord(fit: number): string {
    return fit >= 0.85 ? 'Excellent fit' : fit >= 0.65 ? 'Strong fit' : fit >= 0.4 ? 'Partial fit' : 'Weak fit';
}

/** "n/total met" pill, colour keyed to fit. */
export function FitBadge({ verdicts, fit }: { verdicts?: CriterionVerdict[]; fit?: number }) {
    const total = verdicts?.length ?? 0;
    const f = fit ?? fitOf(verdicts);
    const tone = f >= 0.65 ? VMAP.good : f >= 0.4 ? VMAP.partial : VMAP.no;
    if (total === 0) {
        return (
            <span
                className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
                style={{ background: tone.bg, color: tone.fg }}
            >
                {fitWord(f)}
            </span>
        );
    }
    const met = verdicts!.reduce((s, v) => s + (v.verdict === 'good' ? 1 : v.verdict === 'partial' ? 0.5 : 0), 0);
    const shown = Number.isInteger(met) ? String(met) : met.toFixed(1);
    return (
        <span
            className="inline-flex items-baseline gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums"
            style={{ background: tone.bg, color: tone.fg }}
            title={fitWord(f)}
        >
            {shown}
            <span style={{ opacity: 0.55 }}>/{total}</span>
        </span>
    );
}
